import { FC, useEffect, useState } from 'react';
import PromptInput from '../ui/PromptInput';
import { dbStorage } from '../../utils/dbStorage';
import { toast } from 'sonner';
import { useNavigate } from '@tanstack/react-router';
import { useUser } from '@clerk/clerk-react';

const placeholderPrompts = [
    "Analyze sales trends and create a line chart showing monthly revenue",
    "Compare customer satisfaction scores across different regions with a bar chart",
    "Create a scatter plot to visualize the relationship between marketing spend and sales",
    "Show me the distribution of customer ages using a histogram",
    "Generate a heatmap of website traffic by hour and day of week",
    "Create a pie chart showing market share of different product categories",
    "Analyze customer churn rates and identify key factors",
    "Visualize stock price movements with a candlestick chart",
    "Show me the correlation between different metrics in this dataset",
    "Create a dashboard with key performance indicators"
];

const LandingPrompt: FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholderPrompts[0]);
    const [ellipsis, setEllipsis] = useState('');
    const [isSliding, setIsSliding] = useState(false);
    const navigate = useNavigate();
    const { user, isLoaded: isUserLoaded } = useUser();

    // Handle ellipsis animation
    useEffect(() => {
        const ellipsisInterval = setInterval(() => {
            setEllipsis(prev => {
                if (prev.length >= 3) return '';
                return prev + '.';
            });
        }, 500);

        return () => clearInterval(ellipsisInterval);
    }, []);

    // Handle placeholder text animation
    useEffect(() => {
        let timeout: NodeJS.Timeout;
        
        const cyclePlaceholder = () => {
            setIsSliding(true);
            setCurrentPlaceholder(prev => {
                const currentIndex = placeholderPrompts.indexOf(prev);
                const nextIndex = (currentIndex + 1) % placeholderPrompts.length;
                return placeholderPrompts[nextIndex];
            });
            
            // Reset sliding state after animation
            timeout = setTimeout(() => {
                setIsSliding(false);
            }, 500);
        };

        const interval = setInterval(cyclePlaceholder, 3000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    const handleSubmit = async (prompt: string) => {
        try {
            setIsLoading(true);

            if (isUserLoaded && user) {
                await dbStorage.storePendingPrompt(prompt);
                navigate({ to: '/dashboard' });
            } else {
                // User is not authenticated - store prompt and redirect to sign up
                await dbStorage.storePendingPrompt(prompt);
                navigate({ to: '/sign-up' });
            }
        } catch (error) {
            console.error('Error handling prompt:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStopGeneration = () => {
        setIsLoading(false);
    };

    return (
        <div className="w-full">
            <div className="[&_textarea]:text-sm [&_button]:p-1.5 [&_svg]:w-4 [&_svg]:h-4 [&_.text-\[10px\]]:text-xs [&_.h-6]:h-7">
                <PromptInput
                    onSubmit={handleSubmit}
                    isLoading={isLoading}
                    onStopGeneration={handleStopGeneration}
                    showModelSelection={false}
                    showDataUpload={false}
                    minHeight="100px"
                    maxHeight="300px"
                    defaultRows={5}
                    placeholder={`${currentPlaceholder}${ellipsis}`}
                    className="landing-prompt !bg-transparent"
                    textareaClassName={`landing-textarea text-base leading-7 py-2 placeholder:text-base placeholder:text-gray-500 placeholder:tracking-wide ${isSliding ? 'placeholder:translate-y-[-2px]' : 'placeholder:translate-y-0'} placeholder:transition-transform placeholder:duration-500`}
                    buttonClassName="landing-button"
                    borderColor="border-gray-300"
                />
            </div>
        </div>
    );
};

export default LandingPrompt; 